import React from "react";
import ButtonIcon from "../../../../components/buttons/ButtonIcon";
import CustomTooltip from "../../../../components/CustomTooltip";
import NotInformation from "../../../../components/NotInformation";
import { CustomTextField } from "../../../../components/CustomTextField";
import CustomSearch from "../../../../components/SearchSimples/CustomSearch";
import {
    CheckmarkTitle,
    EditUnderlinedIcon,
    IconPanelIllustration,
    TrashOutlineIcon
} from "../../../../components/buttons/IconButtons";
import SavedLayoutsTabApp from "../Functionality/SavedLayoutsTabApp";
import { LAYOUT_NAME_LENGTH } from "../Model/Model";

/**
 * @Author      Karen Yunuen Pozos Arias
 * @JobTitle    Full Stack
 * @Email       kpozos@ibl.mx
 *
 * Saved Layouts tab: searches, opens, renames or deletes the layouts created by the admin.
 */
const SavedLayoutsTab = ({ activeLayoutId, modified, setModified, onOpen, onRename, onDelete, canUpdate = false, canDelete = false }) => {
    const {
        t,
        layouts,
        searchElement,
        setSearchElement,
        handleSearch,
        handleResetSearch,
        pagination,
        editing,
        startEditing,
        handleChangeName,
        saveName,
        handleKeyDown,
        isValidName
    } = SavedLayoutsTabApp({ modified, setModified, onRename });

    return (
        <div className='d-flex flex-column gap-3'>
            <CustomSearch
                id='txt_slt_search_simple'
                label='search_by_name'
                value={searchElement}
                setValue={setSearchElement}
                onChange={(e) => setSearchElement(e.target.value)}
                searchFunction={handleSearch}
                onReset={handleResetSearch}
            />

            {layouts.length === 0 &&
                <NotInformation
                    text={t(searchElement ? 'no_results_found' : 'you_have_not_created_any_layouts_yet')}
                    card={true}
                />
            }

            {layouts.map((layout) => {
                const isEditing = editing.id === layout.id;

                return (
                    <div
                        key={layout.id}
                        id={`div_slt_layout_${layout.id}`}
                        className={`event-monitoring-saved-layout p-16px border-full-shades-100 border-radius-8px background-shades-50 ${isEditing ? 'event-monitoring-saved-layout-editing' : 'cursor-pointer'} ${layout.id === activeLayoutId ? 'event-monitoring-saved-layout-active' : ''}`}
                        onClick={() => !isEditing && onOpen(layout.id)}
                    >
                        <div className='d-flex align-items-center gap-3'>
                            <IconPanelIllustration className='w-32px h-32px flex-shrink-0' />

                            <div className={`d-flex flex-column flex-grow-1 gap-2 minw-0 ${isEditing ? 'pe-3' : ''}`}>
                                {isEditing
                                    ? <div className='d-flex align-items-start' onClick={(e) => e.stopPropagation()}>
                                        <div className='flex-grow-1 event-monitoring-saved-layout-name-field'>
                                            <CustomTextField
                                                id={`txt_slt_name_${layout.id}`}
                                                length={LAYOUT_NAME_LENGTH}
                                                name='name'
                                                value={editing.name}
                                                trans='name'
                                                type='text'
                                                onChange={handleChangeName}
                                                onKeyDown={handleKeyDown}
                                                required={true}
                                                emptyFields={!isValidName}
                                                showCounter={false}
                                            />
                                        </div>
                                        <ButtonIcon
                                            id={`btn_slt_save_${layout.id}`}
                                            title='save_changes'
                                            Icon={CheckmarkTitle}
                                            className='btn-outlined-success-600 background-general-00 border-radius-right-8px w-40px h-40px minw-40px'
                                            iconProps='btn-icon-svg-24px'
                                            disabled={!isValidName}
                                            onClick={saveName}
                                        />
                                    </div>
                                    : <CustomTooltip
                                        text={layout.name}
                                        classes='fs-14px ibl-font-w-600 font-color-general-950 text-ellipsis-1-row'
                                        showIfTruncated
                                        placement='top'
                                    />
                                }
                                <p className='fs-14px ibl-font-w-400 font-color-shades-950'>
                                    {t('x_panels', { total: layout.totalPanels })}
                                </p>
                            </div>

                            {!isEditing && (canUpdate || canDelete) &&
                                <div className='d-flex align-items-center gap-2' onClick={(e) => e.stopPropagation()}>
                                    {canUpdate &&
                                        <ButtonIcon
                                            id={`btn_slt_edit_${layout.id}`}
                                            title='edit_layout_name'
                                            Icon={EditUnderlinedIcon}
                                            className='btn-outlined-secondary-600-00'
                                            iconProps='btn-icon-svg-16px'
                                            onClick={() => startEditing(layout)}
                                        />
                                    }
                                    {canDelete &&
                                        <ButtonIcon
                                            id={`btn_slt_delete_${layout.id}`}
                                            title='delete'
                                            Icon={TrashOutlineIcon}
                                            className='btn-outlined-error-600'
                                            iconProps='btn-icon-svg-16px'
                                            onClick={() => onDelete(layout)}
                                        />
                                    }
                                </div>
                            }
                        </div>
                    </div>
                );
            })}

            {layouts.length > 0 && pagination()}
        </div>
    );
};

export default SavedLayoutsTab;
